import type { FreeAgentRoom } from './labels'

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** The headline: how many places a free agent could take. */
export function roomHeadline(room: FreeAgentRoom): string {
  if (room.total == null) return 'Room for free agents'
  if (room.total === 0) return 'Starting spots are full'
  return `Up to ${plural(room.total, 'spot')} for free agents`
}

/** Where those places are: open team places, then teams already in that are recruiting. */
export function roomDetail(room: FreeAgentRoom): string {
  const parts: string[] = []
  if (room.openPlaces == null) parts.push('New teams welcome, no cap')
  else if (room.openPlaces > 0 && room.inOpenPlaces) {
    parts.push(`${room.inOpenPlaces} across ${plural(room.openPlaces, 'open team place')}`)
  }
  if (room.onTeams > 0) parts.push(`${room.onTeams} on ${plural(room.recruitingTeams, 'team')} recruiting`)
  return parts.join(' · ')
}

/**
 * The backup line. Starting places are not the only way in: rosters can carry
 * subs, and the pool is where those and any dropouts get filled from — so a
 * free agent hears about it even when no team is recruiting yet.
 */
export function roomBackup(room: FreeAgentRoom): string | null {
  const counted = typeof room.bench === 'number' && room.recruitingTeams > 0 ? room.bench : 0
  if (room.total === 0) {
    if (counted > 0) return `${plural(counted, 'backup spot')} open on teams recruiting. Join the pool to be picked up as a sub.`
    if (room.bench === 'uncapped') return 'Rosters can carry subs. Join the pool to be picked up as a backup.'
    return 'Join the free-agent pool to be first in line if a spot opens.'
  }
  if (counted > 0) return `Plus ${plural(counted, 'backup spot')} on teams recruiting.`
  if (room.bench === 'uncapped') return 'Rosters can carry subs, so the pool can land you a backup spot too.'
  return null
}
